import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Eye, Maximize2, Minimize2 } from "lucide-react";
import { useL } from "@/lib/useL";

const LETTERS = "ABCDEF";
const btn = "inline-flex items-center gap-2 font-mono uppercase tracking-[0.16em] text-xs px-4 py-3 border border-white/40 hover:bg-white hover:text-[#002FA7] transition-colors disabled:opacity-30";

// Board mode: one question at a time, big text, answer shown on click (or always).
export function BoardQuiz({ quiz, onExit }) {
  const L = useL();
  const [i, setI] = useState(0);
  const [shown, setShown] = useState(false);
  const [always, setAlways] = useState(false);
  const [full, setFull] = useState(false);
  const total = quiz.questions.length;
  const q = quiz.questions[i];
  const reveal = always || shown;

  const go = useCallback((d) => { setI((n) => Math.min(total - 1, Math.max(0, n + d))); setShown(false); }, [total]);
  const toggleFull = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())?.catch?.(() => {});

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === " ") { e.preventDefault(); setShown(true); }
    };
    const onFs = () => setFull(!!document.fullscreenElement);
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("fullscreenchange", onFs); };
  }, [go]);

  return (
    <div className="fixed inset-0 z-[90] bg-[#002FA7] text-white flex flex-col" data-testid="board-quiz">
      <div className="flex items-center justify-between gap-3 px-6 py-4 text-sm">
        <span className="font-mono uppercase tracking-[0.2em] truncate">{quiz.title}</span>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="board-always-label">
            <input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} data-testid="board-always-toggle" />
            {L("Réponse toujours visible", "Always show answer")}
          </label>
          <button type="button" onClick={toggleFull} className={btn} data-testid="board-fullscreen">{full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</button>
          <button type="button" onClick={() => { if (document.fullscreenElement) document.exitFullscreen(); onExit(); }} className={btn} data-testid="board-exit">{L("Quitter", "Exit")}</button>
        </div>
      </div>
      <div className="flex-1 overflow-auto px-6 md:px-16 py-6 flex flex-col justify-center max-w-6xl w-full mx-auto">
        <div className="font-mono text-lg opacity-70 mb-4" data-testid="board-progress">{L("Question", "Question")} {i + 1} / {total}</div>
        <h2 className="text-3xl md:text-5xl font-black leading-tight mb-10" style={{ fontFamily: "Outfit" }} data-testid="board-question">{q.q}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {q.options.map((o, j) => {
            const good = reveal && j === q.answer;
            return (
              <div key={j} data-testid={`board-opt-${j}`} className={`border-2 px-6 py-5 text-xl md:text-3xl flex items-center gap-4 transition-colors ${good ? "bg-white text-[#002FA7] border-white" : reveal ? "border-white/20 opacity-50" : "border-white/50"}`}>
                <span className="font-mono font-bold">{LETTERS[j]}</span><span>{o}</span>
              </div>
            );
          })}
        </div>
        {reveal && q.explanation && <p className="mt-8 text-lg md:text-2xl opacity-90" data-testid="board-explanation">{q.explanation}</p>}
      </div>
      <div className="flex items-center justify-between gap-3 px-6 py-5 border-t border-white/20">
        <button type="button" onClick={() => go(-1)} disabled={i === 0} className={btn} data-testid="board-prev"><ChevronLeft className="w-4 h-4" /> {L("Précédente", "Previous")}</button>
        {!always && <button type="button" onClick={() => setShown(true)} disabled={shown} className={`${btn} bg-white text-[#002FA7]`} data-testid="board-reveal"><Eye className="w-4 h-4" /> {L("Afficher la réponse", "Show answer")}</button>}
        <button type="button" onClick={() => go(1)} disabled={i === total - 1} className={btn} data-testid="board-next">{L("Suivante", "Next")} <ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default BoardQuiz;
